import React, { useState } from 'react';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { Mail } from 'lucide-react';
import { ContentHeading1, ContentHeading2, ContentText, ContentWrapper } from '@/components/ui/ContentText';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';

const RecoverPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email) {
      setError('Por favor, informe seu email.');
      return;
    }

    try {
      setSubmitting(true);
      await api.postForgotPassword(email);
      setMessage('Se este email estiver cadastrado, você receberá instruções para redefinir sua senha.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Não foi possível realizar a solicitação. Tente novamente mais tarde.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentWrapper className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <ContentHeading1 className="text-3xl font-bold text-gray-900">Recuperar senha</ContentHeading1>
          <ContentText className="mt-2 text-sm text-gray-600">Informe seu email para receber instruções de recuperação.</ContentText>
        </div>

        <Card className="bg-white shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <ContentHeading2 className="text-xl font-semibold text-gray-900 mb-6">Redefinir senha</ContentHeading2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                  <ContentText className="text-sm text-red-600">{error}</ContentText>
                </div>
              )}

              {message && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md" role="status" aria-live="polite">
                  <ContentText className="text-sm text-green-700">{message}</ContentText>
                </div>
              )}

              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                leftIcon={<Mail className="h-4 w-4" />}
                required
                disabled={submitting}
              />
            </div>

            <Button type="submit" className="w-full" loading={submitting} disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar instruções'}
            </Button>
          </form>
        </Card>

        <div className="text-center">
          <Link to="/login" className="text-sm text-blue-600 hover:underline">Voltar ao login</Link>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default RecoverPasswordPage;
